import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const FeeRateConfigModal = ({ isOpen, onClose, onSave, currentRates }) => {
  const [formData, setFormData] = useState({
    effectiveDate: '',
    feeRate: '',
    currency: 'USD',
    applicableTo: 'all',
    notes: '',
  });

  const [errors, setErrors] = useState({});

  const currencyOptions = [
    { value: 'USD', label: 'USD - US Dollar' },
    { value: 'AUD', label: 'AUD - Australian Dollar' },
    { value: 'INR', label: 'INR - Indian Rupee' },
  ];

  const applicableToOptions = [
    { value: 'all', label: 'All Shipments' },
    { value: 'supplier', label: 'Specific Supplier' },
    { value: 'buyer', label: 'Specific Buyer' },
  ];

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors?.[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.effectiveDate) {
      newErrors.effectiveDate = 'Effective date is required';
    }

    if (!formData?.feeRate || parseFloat(formData?.feeRate) <= 0) {
      newErrors.feeRate = 'Valid fee rate is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSave(formData);
      setFormData({
        effectiveDate: '',
        feeRate: '',
        currency: 'USD',
        applicableTo: 'all',
        notes: '',
      });
      setErrors({});
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Icon name="Settings" size={20} color="#3B82F6" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">
                Fee Rate Configuration
              </h2>
              <p className="text-sm text-slate-600">
                Set new fee rates and effective dates
              </p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} color="#3B82F6" />
              <div>
                <p className="text-sm font-medium text-blue-900 mb-1">
                  Current Active Rate
                </p>
                <p className="text-sm text-blue-700">
                  {currentRates?.current} {currentRates?.currency}/MT effective
                  from {currentRates?.effectiveDate}
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Effective Date"
              type="date"
              value={formData?.effectiveDate}
              onChange={(e) =>
                handleInputChange('effectiveDate', e?.target?.value)
              }
              error={errors?.effectiveDate}
              required
            />

            <Input
              label="Fee Rate"
              type="number"
              placeholder="0.00"
              value={formData?.feeRate}
              onChange={(e) => handleInputChange('feeRate', e?.target?.value)}
              error={errors?.feeRate}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Currency"
              options={currencyOptions}
              value={formData?.currency}
              onChange={(value) => handleInputChange('currency', value)}
            />

            <Select
              label="Applicable To"
              options={applicableToOptions}
              value={formData?.applicableTo}
              onChange={(value) => handleInputChange('applicableTo', value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Notes
            </label>
            <textarea
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              rows="3"
              placeholder="Add any additional notes or context..."
              value={formData?.notes}
              onChange={(e) => handleInputChange('notes', e?.target?.value)}
            />
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="AlertTriangle" size={20} color="#D97706" />
              <div>
                <p className="text-sm font-medium text-yellow-900 mb-1">
                  Important Notice
                </p>
                <p className="text-sm text-yellow-700">
                  New rates will apply to all shipments with ETD on or after
                  the effective date. Existing calculations will not be
                  affected.
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
            <Button variant="outline" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button variant="default" type="submit" iconName="Save">
              Save Configuration
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FeeRateConfigModal;